import { pick } from 'lodash-es';
import { interceptEvent } from './event-interceptor';
import { getAnalyticsMetaProperties } from './meta-properties';
import { sanitizedLocation } from './sanitized-location';
import { Events, type IEvents } from './types';

/**
 * Design Studio callers pass whole domain objects (a node with its HTML parts, a workspace) and rely on the
 * catalog entry to choose what leaves the browser, so its events send only the properties the entry declares.
 * Other events spread what the caller passes: several declare fewer properties than their callers send, and
 * narrowing them would drop data that is recorded today. The two Inbox events are the Design Studio editor's.
 */
function sendsDeclaredPropertiesOnly(eventName: string): boolean {
  return (
    eventName.startsWith('Design Studio: ') ||
    eventName === 'Inbox: Edit Completed' ||
    eventName === 'Inbox Message: Test Sent'
  );
}

function stringsIn(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item) => typeof item === 'string') : [];
}

function getCatalogEntry<Namespace extends keyof IEvents>(
  namespace: Namespace,
  event: keyof IEvents[Namespace] & string,
) {
  const entry: unknown = Events[namespace]?.[event];
  if (typeof entry !== 'object' || entry === null || !('name' in entry) || typeof entry.name !== 'string') {
    return null;
  }
  return {
    name: entry.name,
    declaredProperties: [
      ...stringsIn('properties' in entry ? entry.properties : null),
      ...stringsIn('optionalProperties' in entry ? entry.optionalProperties : null),
    ],
  };
}

/** The wire name of a catalogued event, or `null` when the catalog has no such entry. */
export function getEventName<Namespace extends keyof IEvents>(
  namespace: Namespace,
  event: keyof IEvents[Namespace] & string,
): string | null {
  return getCatalogEntry(namespace, event)?.name ?? null;
}

export type TrackEventBody<Properties extends object> = {
  event: string;
  properties: ReturnType<typeof getAnalyticsMetaProperties> & Partial<Properties>;
};

/**
 * The request body every tracking endpoint takes: the catalogued wire name
 * plus the standard meta block under the per-event properties. Design Studio
 * events send only the properties their entry declares (required or optional);
 * see `sendsDeclaredPropertiesOnly`. `null` for an event the catalog does not know, which callers skip rather
 * than post.
 */
export function buildTrackEventBody<Namespace extends keyof IEvents, Properties extends object>(
  namespace: Namespace,
  event: keyof IEvents[Namespace] & string,
  properties: Properties,
): TrackEventBody<Properties> | null {
  const entry = getCatalogEntry(namespace, event);
  if (entry == null) {
    return null;
  }
  return {
    event: entry.name,
    properties: {
      ...getAnalyticsMetaProperties(),
      ...sanitizedLocation(),
      ...(sendsDeclaredPropertiesOnly(entry.name) ? pick(properties, entry.declaredProperties) : properties),
    },
  };
}

/**
 * Post a catalogued event through the given endpoint. Resolves on success and
 * failure alike, including a throw while building the body — analytics must
 * never block or surface in the app — and skips the post entirely for an event
 * the catalog does not know. An event interceptor (`setEventInterceptor`) sees
 * the event first, and a `null` endpoint posts nothing, for callers with no
 * environment to post to.
 */
export function postCatalogEvent<Namespace extends keyof IEvents, Properties extends object, Response>(
  namespace: Namespace,
  event: keyof IEvents[Namespace] & string,
  properties: Properties,
  post: ((body: TrackEventBody<Properties>) => Promise<Response>) | null,
): Promise<void> {
  // The body reads browser globals (the GA client id, cookies) that a third-party script can make throw.
  try {
    const body = buildTrackEventBody(namespace, event, properties);
    if (body == null) {
      return Promise.resolve();
    }
    if (interceptEvent(body)) {
      return Promise.resolve();
    }
    if (post == null) {
      return Promise.resolve();
    }
    return post(body).then(
      () => undefined,
      () => undefined,
    );
  } catch {
    return Promise.resolve();
  }
}
